import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { isLscCfbDynastySetupEnabled } from '../../config/featureFlags';
import {
  createCfbAssignment,
  createCfbSeason,
  getCfbSetupCompleteness,
  listCfbAssignments,
  listCfbSeasons,
  listLscMembers,
  LscApiError,
  updateCfbAssignment,
  updateCfbSeason,
} from '../api';
import CommissionerOnlyGate from '../components/CommissionerOnlyGate';
import CfbSchoolPicker from '../components/CfbSchoolPicker';
import CfbSchoolListRow from '../components/CfbSchoolListRow';
import ErrorBanner from '../components/ErrorBanner';
import LoadingState from '../components/LoadingState';
import {
  applyPrimaryPlatformToPayload,
  assignmentToFormPayload,
  buildAssignmentUpdatePayload,
  CFB_ADVANCE_CADENCE_OPTIONS,
  CFB_PLATFORM_POLICY_OPTIONS,
  CFB_PRIMARY_PLATFORM_OPTIONS,
  deriveCrossplayEnabled,
  extractMemberDiscordDisplay,
  extractMemberTwitchLogin,
  formatCfbCompletenessCustomerMessage,
  getUnassignedMembersForAdd,
  inferPrimaryPlatformFromAssignment,
  parseAdvanceCadence,
  resolveMemberDisplayLabel,
  parsePlatformPolicy,
  platformIdLabel,
  serializeAdvanceCadence,
  serializePlatformPolicy,
  type CfbAdvanceCadenceSelect,
  type CfbPlatformPolicySelect,
  type CfbPrimaryPlatform,
} from '../cfbSetupFormUtils';
import type {
  CfbAssignmentPayload,
  CfbDynastyAssignment,
  CfbDynastySeason,
  CfbSetupCompleteness,
} from '../cfbSetupTypes';
import type { CfbSchoolCatalogEntry } from '../cfbSchoolCatalogTypes';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import { useLeague } from '../hooks/useLeague';
import { useCfbSchoolCatalog } from '../hooks/useCfbSchoolCatalog';
import { resolveSchoolFromIndex, type CfbCatalogIndex } from '../cfbBranding/cfbTeamVisualIndex';
import type { LscMember } from '../types';
import { lscReturnTo } from '../routing';
import { useLscContentColumn } from '../lscContentColumn';

const emptyAssignmentForm: CfbAssignmentPayload = {
  member_id: '',
  school_name: '',
  school_abbreviation: '',
  mascot: '',
  conference: '',
  platform: '',
  platform_handle: '',
  ea_id: '',
  xbox_gamertag: '',
  psn_id: '',
  steam_id: '',
  epic_id: '',
  twitch_login: '',
  discord_handle: '',
  is_user_controlled: true,
  assignment_confidence: 'commissioner_confirmed',
};

const LscCfbSetupPage: React.FC = () => {
  useLscContentColumn('narrow');
  const { leagueId } = useParams<{ leagueId: string }>();
  return (
    <LscAuthGate returnTo={lscReturnTo(`/leagues/${leagueId}/cfb/setup`)}>
      <CommissionerOnlyGate>
        <CfbSetupContent leagueId={leagueId ?? ''} />
      </CommissionerOnlyGate>
    </LscAuthGate>
  );
};

function memberLabel(member: LscMember): string {
  return resolveMemberDisplayLabel({ member });
}

function catalogEntryFromAssignment(
  assignment: CfbDynastyAssignment,
  catalogIndex?: CfbCatalogIndex | null
): CfbSchoolCatalogEntry | null {
  if (!assignment.school_id) {
    return null;
  }
  const resolved = catalogIndex
    ? resolveSchoolFromIndex(catalogIndex, { schoolId: assignment.school_id })
    : null;
  const base: CfbSchoolCatalogEntry = {
    school_id: assignment.school_id,
    display_name: assignment.school_display_name_snapshot || assignment.school_name,
    athletic_name: assignment.school_athletic_name_snapshot || assignment.school_name,
    short_name: assignment.school_short_name_snapshot || assignment.school_name,
    initials: assignment.school_initials_snapshot || assignment.school_abbreviation || '',
    conference_key: assignment.school_conference_key_snapshot || '',
    conference_name: assignment.school_conference_name_snapshot || assignment.conference || '',
    aliases: [],
    subdivision: '',
    active_status: 'active',
    visual_type: 'initials',
    logo_status: 'none',
    source_notes: '',
    created_at: '',
    updated_at: '',
  };
  if (!resolved) {
    return base;
  }
  return {
    ...base,
    primary_color: resolved.primary_color,
    secondary_color: resolved.secondary_color,
    tertiary_color: resolved.tertiary_color,
    quaternary_color: resolved.quaternary_color,
    colors_verified: resolved.colors_verified,
    conference_key: base.conference_key || resolved.conference_key,
    conference_name: base.conference_name || resolved.conference_name,
    initials: base.initials || resolved.initials,
  };
}

function assignmentHasAdvancedFields(assignment: CfbDynastyAssignment): boolean {
  return Boolean(
    assignment.mascot ||
      assignment.steam_id ||
      assignment.epic_id ||
      (assignment.ea_id && !assignment.xbox_gamertag && !assignment.psn_id) ||
      (assignment.xbox_gamertag && assignment.psn_id)
  );
}

function CfbSetupContent({ leagueId }: { leagueId: string }) {
  const { league } = useLeague();
  const cfbCatalog = useCfbSchoolCatalog();
  const featureEnabled = isLscCfbDynastySetupEnabled();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [season, setSeason] = useState<CfbDynastySeason | null>(null);
  const [assignments, setAssignments] = useState<CfbDynastyAssignment[]>([]);
  const [members, setMembers] = useState<LscMember[]>([]);
  const [completeness, setCompleteness] = useState<CfbSetupCompleteness | null>(null);
  const [savingSeason, setSavingSeason] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [seasonSaveFeedback, setSeasonSaveFeedback] = useState<{
    kind: 'success' | 'error';
    message: string;
  } | null>(null);

  const [seasonForm, setSeasonForm] = useState({
    display_name: '',
    game_title: 'College Football 27',
    season_number: 1,
    regular_season_weeks: 12,
    advance_cadence: '48_hours',
    platform_policy: 'crossplay',
    crossplay_enabled: true,
    playoff_enabled: true,
    commissioner_notes: '',
  });
  const [advanceCadenceSelect, setAdvanceCadenceSelect] = useState<CfbAdvanceCadenceSelect>('48_hours');
  const [advanceCadenceCustom, setAdvanceCadenceCustom] = useState('');
  const [platformPolicySelect, setPlatformPolicySelect] = useState<CfbPlatformPolicySelect>('crossplay');
  const [platformPolicyCustom, setPlatformPolicyCustom] = useState('');

  const [assignmentForm, setAssignmentForm] = useState<CfbAssignmentPayload>(emptyAssignmentForm);
  const [primaryPlatform, setPrimaryPlatform] = useState<CfbPrimaryPlatform>('unknown');
  const [platformId, setPlatformId] = useState('');
  const [selectedSchool, setSelectedSchool] = useState<CfbSchoolCatalogEntry | null>(null);
  const [manualSchoolOpen, setManualSchoolOpen] = useState(false);
  const [advancedAssignmentOpen, setAdvancedAssignmentOpen] = useState(false);
  const [editingAssignmentId, setEditingAssignmentId] = useState<string | null>(null);
  const [assignmentSaveFeedback, setAssignmentSaveFeedback] = useState<{
    kind: 'success' | 'error';
    message: string;
  } | null>(null);

  const activeMembers = useMemo(
    () => members.filter((member) => member.status === 'active' || member.status === 'imported_unclaimed'),
    [members]
  );

  const activeAssignments = useMemo(
    () => assignments.filter((item) => item.active_status === 'active'),
    [assignments]
  );

  const unassignedMembers = useMemo(
    () => getUnassignedMembersForAdd(activeMembers, assignments),
    [activeMembers, assignments]
  );

  const membersForSelect = useMemo(() => {
    if (editingAssignmentId) {
      const current = activeMembers.find((member) => member.id === assignmentForm.member_id);
      return current ? [current] : [];
    }
    return unassignedMembers;
  }, [activeMembers, assignmentForm.member_id, editingAssignmentId, unassignedMembers]);

  const selectedMember = useMemo(
    () => activeMembers.find((member) => member.id === assignmentForm.member_id) ?? null,
    [activeMembers, assignmentForm.member_id]
  );
  const memberById = useMemo(
    () => new Map(members.map((member) => [member.id, member])),
    [members]
  );

  const memberDiscord = useMemo(() => extractMemberDiscordDisplay(selectedMember), [selectedMember]);
  const memberTwitch = useMemo(() => extractMemberTwitchLogin(selectedMember), [selectedMember]);

  const refreshCompleteness = useCallback(
    async (seasonId: string) => {
      const result = await getCfbSetupCompleteness(leagueId, seasonId);
      setCompleteness(result.completeness);
    },
    [leagueId]
  );

  const loadSeasonBundle = useCallback(
    async (selected: CfbDynastySeason) => {
      const seasonId = selected.season_id;
      const assignmentResult = await listCfbAssignments(leagueId, seasonId);
      setAssignments(assignmentResult.assignments);
      await refreshCompleteness(seasonId);
    },
    [leagueId, refreshCompleteness]
  );

  const applySeasonFormValues = useCallback((values: typeof seasonForm) => {
    const cadence = parseAdvanceCadence(values.advance_cadence);
    const policy = parsePlatformPolicy(values.platform_policy);
    setAdvanceCadenceSelect(cadence.select);
    setAdvanceCadenceCustom(cadence.custom);
    setPlatformPolicySelect(policy.select);
    setPlatformPolicyCustom(policy.custom);
    setSeasonForm(values);
  }, []);

  const loadInitial = useCallback(async () => {
    if (!featureEnabled || !leagueId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [seasonList, memberList] = await Promise.all([
        listCfbSeasons(leagueId),
        listLscMembers(leagueId),
      ]);
      setMembers(memberList.members);
      const existing = seasonList.seasons[0] ?? null;
      setSeason(existing);
      if (existing) {
        applySeasonFormValues({
          display_name: existing.display_name,
          game_title: existing.game_title,
          season_number: existing.season_number,
          regular_season_weeks: existing.regular_season_weeks,
          advance_cadence: existing.advance_cadence,
          platform_policy: existing.platform_policy,
          crossplay_enabled: existing.crossplay_enabled,
          playoff_enabled: existing.playoff_enabled,
          commissioner_notes: existing.commissioner_notes ?? '',
        });
        await loadSeasonBundle(existing);
      }
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Failed to load CFB setup.');
    } finally {
      setLoading(false);
    }
  }, [applySeasonFormValues, featureEnabled, leagueId, loadSeasonBundle]);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  useEffect(() => {
    if (editingAssignmentId) {
      return;
    }
    if (!selectedMember) {
      setAssignmentForm((current) => ({
        ...current,
        twitch_login: '',
        discord_handle: '',
      }));
      return;
    }
    setAssignmentForm((current) => ({
      ...current,
      twitch_login: extractMemberTwitchLogin(selectedMember),
      discord_handle: extractMemberDiscordDisplay(selectedMember).storedValue,
    }));
  }, [selectedMember, editingAssignmentId]);

  const buildSeasonPayload = () => {
    const advance_cadence = serializeAdvanceCadence(advanceCadenceSelect, advanceCadenceCustom);
    const platform_policy = serializePlatformPolicy(platformPolicySelect, platformPolicyCustom);
    return {
      ...seasonForm,
      advance_cadence,
      platform_policy,
      crossplay_enabled: deriveCrossplayEnabled(platform_policy),
    };
  };

  const handleCreateSeason = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingSeason(true);
    setSeasonSaveFeedback(null);
    setError(null);
    try {
      const result = await createCfbSeason(leagueId, buildSeasonPayload());
      setSeason(result.season);
      await loadSeasonBundle(result.season);
      setSeasonSaveFeedback({ kind: 'success', message: 'Season created successfully' });
    } catch (err) {
      const message = err instanceof LscApiError ? err.message : 'Failed to create season.';
      setSeasonSaveFeedback({ kind: 'error', message });
      setError(message);
    } finally {
      setSavingSeason(false);
    }
  };

  const handleSaveSeason = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!season) return;
    setSavingSeason(true);
    setSeasonSaveFeedback(null);
    setError(null);
    try {
      const result = await updateCfbSeason(leagueId, season.season_id, buildSeasonPayload());
      setSeason(result.season);
      await refreshCompleteness(season.season_id);
      setSeasonSaveFeedback({ kind: 'success', message: 'Changes saved successfully' });
    } catch (err) {
      const message = err instanceof LscApiError ? err.message : 'Failed to update season.';
      setSeasonSaveFeedback({ kind: 'error', message });
      setError(message);
    } finally {
      setSavingSeason(false);
    }
  };

  const resetAssignmentForm = () => {
    setAssignmentForm(emptyAssignmentForm);
    setSelectedSchool(null);
    setPrimaryPlatform('unknown');
    setPlatformId('');
    setManualSchoolOpen(false);
    setAdvancedAssignmentOpen(false);
    setEditingAssignmentId(null);
    setAssignmentSaveFeedback(null);
  };

  const populateAssignmentForm = (assignment: CfbDynastyAssignment) => {
    const formPayload = assignmentToFormPayload(assignment);
    const platform = inferPrimaryPlatformFromAssignment(assignment);
    const catalogSchool = catalogEntryFromAssignment(assignment, cfbCatalog?.index);

    setAssignmentForm(formPayload);
    setPrimaryPlatform(platform.platform);
    setPlatformId(platform.platformId);
    setSelectedSchool(catalogSchool);
    setManualSchoolOpen(!catalogSchool && Boolean(formPayload.school_name));
    setAdvancedAssignmentOpen(assignmentHasAdvancedFields(assignment));
    setEditingAssignmentId(assignment.id);
    setAssignmentSaveFeedback(null);
  };

  const handleEditAssignment = (assignment: CfbDynastyAssignment) => {
    populateAssignmentForm(assignment);
  };

  const handleCancelEdit = () => {
    resetAssignmentForm();
  };

  const hasAssignmentSchool = Boolean(
    selectedSchool || manualSchoolOpen || assignmentForm.school_name?.trim()
  );

  const handleSaveAssignment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!season) return;
    if (!editingAssignmentId && membersForSelect.length === 0) {
      return;
    }
    setSavingAssignment(true);
    setError(null);
    setAssignmentSaveFeedback(null);
    try {
      if (editingAssignmentId) {
        const patch = buildAssignmentUpdatePayload(
          assignmentToFormPayload(assignmentForm),
          primaryPlatform,
          platformId,
          selectedSchool,
          manualSchoolOpen
        );
        await updateCfbAssignment(leagueId, season.season_id, editingAssignmentId, patch);
        resetAssignmentForm();
        await loadSeasonBundle(season);
        setAssignmentSaveFeedback({
          kind: 'success',
          message: 'Assignment updated successfully',
        });
        return;
      }

      const payload: CfbAssignmentPayload = { ...assignmentForm };
      applyPrimaryPlatformToPayload(payload, primaryPlatform, platformId);
      if (selectedSchool) {
        payload.school_id = selectedSchool.school_id;
        payload.school_name = selectedSchool.athletic_name;
        payload.school_abbreviation = selectedSchool.initials;
        payload.conference = selectedSchool.conference_name;
      } else {
        delete payload.school_id;
      }
      await createCfbAssignment(leagueId, season.season_id, payload);
      resetAssignmentForm();
      await loadSeasonBundle(season);
      setAssignmentSaveFeedback({
        kind: 'success',
        message: 'Assignment added successfully',
      });
    } catch (err) {
      const message = err instanceof LscApiError ? err.message : 'Failed to save assignment.';
      setAssignmentSaveFeedback({ kind: 'error', message });
      setError(message);
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleAssignmentSchoolFieldChange = (
    field: 'school_name' | 'school_abbreviation' | 'conference',
    value: string
  ) => {
    setSelectedSchool(null);
    setAssignmentForm((current) => ({
      ...current,
      school_id: undefined,
      [field]: value,
    }));
  };

  const insufficientAssignments =
    completeness?.missing.some((item) => item.code === 'insufficient_user_assignments') ?? false;
  const visibleMissing = (completeness?.missing ?? []).filter(
    (item) => item.code !== 'insufficient_user_assignments'
  );

  if (!featureEnabled) {
    return (
      <div className="card p-6" data-testid="cfb-setup-disabled">
        <h1 className="text-2xl font-bold text-white mb-2">CFB Dynasty Setup</h1>
        <p className="text-secondary-400 text-sm">
          CFB Dynasty Setup is not enabled for this environment yet. No league behavior has changed.
        </p>
      </div>
    );
  }

  if (loading) {
    return <LoadingState message="Loading CFB dynasty setup…" />;
  }

  return (
    <div className="space-y-8" data-testid="cfb-setup-page">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">CFB Dynasty Setup</h1>
        <p className="text-sm text-secondary-400 max-w-3xl">
          Configure your dynasty season and assign members to schools. LSC uses these records for schedule
          matching and VOD verification.
        </p>
        {league?.game_key !== 'cfb' ? (
          <p className="text-xs text-amber-300 mt-2">
            This league is not marked as CFB (`game_key={league?.game_key ?? 'unknown'}`), but setup data
            can still be captured for testing.
          </p>
        ) : null}
      </div>

      {error ? <ErrorBanner message={error} /> : null}

      <section className="card p-6 space-y-4" data-testid="cfb-season-form">
        <h2 className="text-lg font-semibold text-white">Season Profile</h2>
        <form className="grid gap-4 md:grid-cols-2" onSubmit={season ? handleSaveSeason : handleCreateSeason}>
          <label className="block md:col-span-2">
            <span className="text-sm text-secondary-300">Dynasty season name</span>
            <input
              className="input-field mt-1 w-full"
              value={seasonForm.display_name}
              onChange={(e) => setSeasonForm({ ...seasonForm, display_name: e.target.value })}
              required
            />
            <span className="mt-1 block text-xs text-secondary-500">
              Used to group schedules, school assignments, VOD matching, and archive history for this dynasty
              season.
            </span>
          </label>
          <label className="block">
            <span className="text-sm text-secondary-300">Game title</span>
            <input
              className="input-field mt-1 w-full"
              value={seasonForm.game_title}
              onChange={(e) => setSeasonForm({ ...seasonForm, game_title: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="text-sm text-secondary-300">Season number</span>
            <input
              type="number"
              className="input-field mt-1 w-full"
              value={seasonForm.season_number}
              onChange={(e) =>
                setSeasonForm({ ...seasonForm, season_number: Number(e.target.value) || 1 })
              }
            />
          </label>
          <label className="block">
            <span className="text-sm text-secondary-300">Regular season weeks</span>
            <input
              type="number"
              className="input-field mt-1 w-full"
              value={seasonForm.regular_season_weeks}
              onChange={(e) =>
                setSeasonForm({
                  ...seasonForm,
                  regular_season_weeks: Number(e.target.value) || 12,
                })
              }
            />
          </label>
          <label className="block">
            <span className="text-sm text-secondary-300">Advance cadence</span>
            <select
              className="input-field mt-1 w-full"
              value={advanceCadenceSelect}
              onChange={(e) => setAdvanceCadenceSelect(e.target.value as CfbAdvanceCadenceSelect)}
              data-testid="cfb-advance-cadence-select"
            >
              {CFB_ADVANCE_CADENCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {advanceCadenceSelect === 'other' ? (
            <label className="block">
              <span className="text-sm text-secondary-300">Custom advance cadence</span>
              <input
                className="input-field mt-1 w-full"
                value={advanceCadenceCustom}
                onChange={(e) => setAdvanceCadenceCustom(e.target.value)}
                placeholder="Describe your advance schedule"
                data-testid="cfb-advance-cadence-custom"
              />
            </label>
          ) : null}
          <label className="block">
            <span className="text-sm text-secondary-300">Platform policy</span>
            <select
              className="input-field mt-1 w-full"
              value={platformPolicySelect}
              onChange={(e) => setPlatformPolicySelect(e.target.value as CfbPlatformPolicySelect)}
              data-testid="cfb-platform-policy-select"
            >
              {CFB_PLATFORM_POLICY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {platformPolicySelect === 'other' ? (
            <label className="block">
              <span className="text-sm text-secondary-300">Custom platform policy</span>
              <input
                className="input-field mt-1 w-full"
                value={platformPolicyCustom}
                onChange={(e) => setPlatformPolicyCustom(e.target.value)}
                placeholder="Describe your platform rules"
                data-testid="cfb-platform-policy-custom"
              />
            </label>
          ) : null}
          <label className="flex items-center gap-2 md:col-span-2">
            <input
              type="checkbox"
              checked={seasonForm.playoff_enabled}
              onChange={(e) => setSeasonForm({ ...seasonForm, playoff_enabled: e.target.checked })}
            />
            <span className="text-sm text-secondary-300">Playoff enabled</span>
          </label>
          <label className="block md:col-span-2">
            <span className="text-sm text-secondary-300">Commissioner notes</span>
            <textarea
              className="input-field mt-1 w-full min-h-[80px]"
              value={seasonForm.commissioner_notes}
              onChange={(e) => setSeasonForm({ ...seasonForm, commissioner_notes: e.target.value })}
            />
          </label>
          <div className="md:col-span-2 space-y-2">
            <button type="submit" className="btn-primary" disabled={savingSeason} data-testid="cfb-season-save-button">
              {season ? (savingSeason ? 'Saving…' : 'Save Season') : savingSeason ? 'Creating…' : 'Create Season'}
            </button>
            {seasonSaveFeedback?.kind === 'success' ? (
              <p
                role="status"
                className="text-sm text-green-300"
                data-testid="cfb-season-save-success"
              >
                {seasonSaveFeedback.message}
              </p>
            ) : null}
            {seasonSaveFeedback?.kind === 'error' ? (
              <p
                role="alert"
                className="text-sm text-red-300"
                data-testid="cfb-season-save-error"
              >
                {seasonSaveFeedback.message}
              </p>
            ) : null}
          </div>
        </form>
      </section>

      {season ? (
        <>
          <section className="card p-6 space-y-4" data-testid="cfb-completeness-panel">
            <h2 className="text-lg font-semibold text-white">Setup Completeness</h2>
            {completeness ? (
              <div className="space-y-3">
                {insufficientAssignments ? (
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100">
                    <p className="font-medium" data-testid="cfb-setup-readiness-title">
                      Setup in progress
                    </p>
                    <p className="mt-1 text-amber-100/90" data-testid="cfb-setup-readiness-copy">
                      Add at least two active school assignments before this dynasty can be used for schedule
                      and VOD verification.
                    </p>
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-4 text-sm text-secondary-300">
                  <span data-testid="cfb-completeness-status">
                    Status: {completeness.status === 'incomplete' ? 'Setup in progress' : completeness.status}
                  </span>
                  <span>Score: {completeness.score}%</span>
                  <span>
                    Required: {completeness.required_complete}/{completeness.required_total}
                  </span>
                </div>
                {completeness.next_recommended_action && !insufficientAssignments ? (
                  <p className="text-secondary-300 text-sm">{completeness.next_recommended_action}</p>
                ) : null}
                {visibleMissing.length > 0 ? (
                  <ul className="text-sm text-amber-200 space-y-1" data-testid="cfb-completeness-missing">
                    {visibleMissing.map((item) => (
                      <li key={item.code}>{item.message}</li>
                    ))}
                  </ul>
                ) : null}
                {completeness.warnings.length > 0 ? (
                  <ul className="text-sm text-amber-200/90 space-y-1" data-testid="cfb-completeness-warnings">
                    {completeness.warnings.map((item) => (
                      <li key={item.code}>{formatCfbCompletenessCustomerMessage(item)}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : (
              <p className="text-secondary-400 text-sm">Completeness will appear after season creation.</p>
            )}
          </section>

          <section className="card p-6 space-y-4" data-testid="cfb-assignment-manager">
            <h2 className="text-lg font-semibold text-white">School Assignments</h2>
            <form className="grid gap-3 md:grid-cols-2" onSubmit={handleSaveAssignment}>
              <label className="block md:col-span-2">
                <span className="text-sm text-secondary-300">Member</span>
                <select
                  className="input-field mt-1 w-full"
                  value={assignmentForm.member_id}
                  onChange={(e) =>
                    setAssignmentForm({ ...assignmentForm, member_id: e.target.value })
                  }
                  required
                  disabled={Boolean(editingAssignmentId)}
                  data-testid="cfb-assignment-member-select"
                >
                  <option value="">
                    {editingAssignmentId ? 'Assigned member' : 'Select member'}
                  </option>
                  {membersForSelect.map((member) => (
                    <option key={member.id} value={member.id}>
                      {memberLabel(member)}
                    </option>
                  ))}
                </select>
                {editingAssignmentId ? (
                  <span className="mt-1 block text-xs text-secondary-500" data-testid="cfb-assignment-member-locked-copy">
                    Member cannot be changed after assignment creation. Assignment reassignment will be
                    handled in a future safe removal flow.
                  </span>
                ) : null}
                {!editingAssignmentId && activeMembers.length > 0 && unassignedMembers.length === 0 ? (
                  <p
                    className="mt-2 text-sm text-secondary-400"
                    data-testid="cfb-assignment-all-members-assigned"
                  >
                    All current members already have school assignments.
                  </p>
                ) : null}
              </label>

              <div className="md:col-span-2">
                <CfbSchoolPicker
                  selectedSchool={selectedSchool}
                  onSelect={(school) => {
                    setSelectedSchool(school);
                    setManualSchoolOpen(false);
                    setAssignmentForm((current) => ({
                      ...current,
                      school_id: school.school_id,
                      school_name: school.athletic_name,
                      school_abbreviation: school.initials,
                      conference: school.conference_name,
                    }));
                  }}
                  onClear={() => {
                    setSelectedSchool(null);
                    setAssignmentForm((current) => ({
                      ...current,
                      school_id: undefined,
                      school_name: '',
                      school_abbreviation: '',
                      conference: '',
                    }));
                  }}
                />
              </div>

              <div className="md:col-span-2">
                <button
                  type="button"
                  className="text-sm text-secondary-400 hover:text-white"
                  onClick={() => setManualSchoolOpen((open) => !open)}
                  data-testid="cfb-manual-school-toggle"
                >
                  Can&apos;t find a school?
                </button>
              </div>

              {manualSchoolOpen ? (
                <>
                  <label className="block md:col-span-2">
                    <span className="text-sm text-secondary-300">Manual school name</span>
                    <input
                      className="input-field mt-1 w-full"
                      value={assignmentForm.school_name ?? ''}
                      onChange={(e) => handleAssignmentSchoolFieldChange('school_name', e.target.value)}
                      required={manualSchoolOpen && !selectedSchool}
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm text-secondary-300">Abbreviation</span>
                    <input
                      className="input-field mt-1 w-full"
                      value={assignmentForm.school_abbreviation ?? ''}
                      onChange={(e) =>
                        handleAssignmentSchoolFieldChange('school_abbreviation', e.target.value)
                      }
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm text-secondary-300">Conference</span>
                    <input
                      className="input-field mt-1 w-full"
                      value={assignmentForm.conference ?? ''}
                      onChange={(e) => handleAssignmentSchoolFieldChange('conference', e.target.value)}
                    />
                  </label>
                </>
              ) : null}

              <label className="block">
                <span className="text-sm text-secondary-300">Primary platform</span>
                <select
                  className="input-field mt-1 w-full"
                  value={primaryPlatform}
                  onChange={(e) => setPrimaryPlatform(e.target.value as CfbPrimaryPlatform)}
                  data-testid="cfb-assignment-platform-select"
                >
                  {CFB_PRIMARY_PLATFORM_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-sm text-secondary-300">{platformIdLabel(primaryPlatform)}</span>
                <input
                  className="input-field mt-1 w-full"
                  value={platformId}
                  onChange={(e) => setPlatformId(e.target.value)}
                  data-testid="cfb-assignment-platform-id"
                />
              </label>

              <div className="block md:col-span-2">
                <span className="text-sm text-secondary-300">Twitch</span>
                <p className="mt-1 text-sm text-secondary-200" data-testid="cfb-assignment-twitch-copy">
                  {memberTwitch ? `Twitch: ${memberTwitch}` : 'Twitch not linked for this member'}
                </p>
                {!memberTwitch ? (
                  <input
                    className="input-field mt-2 w-full"
                    value={assignmentForm.twitch_login ?? ''}
                    onChange={(e) =>
                      setAssignmentForm({ ...assignmentForm, twitch_login: e.target.value })
                    }
                    placeholder="Enter Twitch login"
                  />
                ) : null}
              </div>

              <div className="block md:col-span-2">
                <span className="text-sm text-secondary-300">Discord</span>
                <p className="mt-1 text-sm text-secondary-200" data-testid="cfb-assignment-discord-copy">
                  {memberDiscord.connected && memberDiscord.handle
                    ? `Discord: connected as ${memberDiscord.handle}`
                    : memberDiscord.connected
                      ? 'Discord: connected'
                      : 'Discord not linked'}
                </p>
                {!memberDiscord.connected ? (
                  <input
                    className="input-field mt-2 w-full"
                    value={assignmentForm.discord_handle ?? ''}
                    onChange={(e) =>
                      setAssignmentForm({ ...assignmentForm, discord_handle: e.target.value })
                    }
                    placeholder="Enter Discord handle"
                  />
                ) : null}
              </div>

              <div className="md:col-span-2">
                <button
                  type="button"
                  className="text-sm text-secondary-400 hover:text-white"
                  onClick={() => setAdvancedAssignmentOpen((open) => !open)}
                  data-testid="cfb-assignment-advanced-toggle"
                >
                  {advancedAssignmentOpen ? 'Hide advanced fields' : 'Advanced fields'}
                </button>
              </div>

              {advancedAssignmentOpen ? (
                <>
                  {(
                    [
                      ['mascot', 'Mascot'],
                      ['ea_id', 'EA ID'],
                      ['xbox_gamertag', 'Xbox gamertag'],
                      ['psn_id', 'PSN ID'],
                      ['steam_id', 'Steam ID'],
                      ['epic_id', 'Epic ID'],
                    ] as const
                  ).map(([field, label]) => (
                    <label className="block" key={field}>
                      <span className="text-sm text-secondary-300">{label}</span>
                      <input
                        className="input-field mt-1 w-full"
                        value={String(assignmentForm[field] ?? '')}
                        onChange={(e) =>
                          setAssignmentForm({ ...assignmentForm, [field]: e.target.value })
                        }
                      />
                    </label>
                  ))}
                </>
              ) : null}

              <div className="md:col-span-2 flex flex-wrap items-center gap-3">
                <button
                  type="submit"
                  className="btn-secondary"
                  disabled={
                    savingAssignment ||
                    !hasAssignmentSchool ||
                    (!editingAssignmentId && membersForSelect.length === 0)
                  }
                  data-testid="cfb-assignment-submit-button"
                >
                  {savingAssignment
                    ? 'Saving…'
                    : editingAssignmentId
                      ? 'Update Assignment'
                      : 'Add Assignment'}
                </button>
                {editingAssignmentId ? (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleCancelEdit}
                    data-testid="cfb-assignment-cancel-edit-button"
                  >
                    Cancel edit
                  </button>
                ) : null}
              </div>
              {assignmentSaveFeedback?.kind === 'success' ? (
                <p
                  role="status"
                  className="md:col-span-2 text-sm text-green-300"
                  data-testid="cfb-assignment-save-success"
                >
                  {assignmentSaveFeedback.message}
                </p>
              ) : null}
              {assignmentSaveFeedback?.kind === 'error' ? (
                <p
                  role="alert"
                  className="md:col-span-2 text-sm text-red-300"
                  data-testid="cfb-assignment-save-error"
                >
                  {assignmentSaveFeedback.message}
                </p>
              ) : null}
            </form>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-secondary-400 text-left">
                    <th className="py-2 pr-4">School</th>
                    <th className="py-2 pr-4">Member</th>
                    <th className="py-2 pr-4">Twitch</th>
                    <th className="py-2 pr-4">Platform IDs</th>
                    <th className="py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeAssignments.map((item) => {
                    const schoolEntry = catalogEntryFromAssignment(item, cfbCatalog?.index);
                    return (
                      <tr key={item.id} className="border-t border-secondary-800">
                        <td className="py-2 pr-4">
                          {schoolEntry ? (
                            <CfbSchoolListRow school={schoolEntry} className="rounded-md !py-1.5 !pl-2" />
                          ) : (
                            item.school_name
                          )}
                        </td>
                        <td
                          className="py-2 pr-4"
                          data-testid={`cfb-assignment-member-label-${item.id}`}
                        >
                          {resolveMemberDisplayLabel({
                            member: memberById.get(item.member_id),
                            assignment: item,
                          })}
                        </td>
                        <td className="py-2 pr-4">{item.twitch_login ?? '—'}</td>
                        <td className="py-2 pr-4">
                          {[item.ea_id, item.xbox_gamertag, item.psn_id, item.platform_handle]
                            .filter(Boolean)
                            .join(' / ') || '—'}
                        </td>
                        <td className="py-2">
                          <button
                            type="button"
                            className="btn-secondary text-xs"
                            onClick={() => handleEditAssignment(item)}
                            data-testid={`cfb-assignment-edit-${item.id}`}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

export default LscCfbSetupPage;
